import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { OrderSummaryCard } from 'components/OrderSummaryCard';
import { FormInput } from 'components/ui/payForm';
import useTurkeyCities from 'use-turkey-cities';
import SimpleHeader from 'components/SimpleHeader';
import { Button } from 'components/ui/button';
import { getAllOrganizations } from '../services/apiService';
import {
  PACKAGE_CATEGORIES,
  PRODUCTS,
  MASA_KARTI_TASARIMLARI,
  EL_KARTI_TASARIMLARI
} from '../data/packages';
import { v4 as uuidv4 } from 'uuid';
interface ProductWithId { id: string; name: string; price: number; freeTableCardQty?: number; freeHandCardQty?: number; freeBoardQty?: number; }
interface ShippingDetails { name: string; cift: string; email: string; eventDate: string; phone: string; address: string; city: string; town: string; postalCode: string; note?: string; organizationName?: string; }
interface OrderDetailsForDisplay { packageName: string; packagePrice: number; items: { name: string; quantity: number; subtotal: number }[]; subtotal: number; tax: number; shippingFee?: number; total: number; }


interface Organization {
  _id: string; // MongoDB'den geleceği için _id
  name: string;
}

const generateUniqueCustomerId = (): string => {
  const STORAGE_KEY = 'anIkareCustomerId';
  let customerId = localStorage.getItem(STORAGE_KEY);

  // Eğer customerId yoksa, yeni bir tane oluştur
  if (!customerId) {
    // uuidv4() ile benzersiz bir ID oluşturuyoruz
    customerId = uuidv4();
    localStorage.setItem(STORAGE_KEY, customerId);
  }

  // Not: Gerçek bir uygulamada, giriş yapmış kullanıcılar için
  // buraya user ID'yi (örneğin auth.userId) alma mantığı eklenmelidir.
  return customerId;
};

export default function AdresBilgileriPage() {
  const navigate = useNavigate();
  const { cities, city, setCity, districts, district, setDistrict } = useTurkeyCities();

  const [orderDetails, setOrderDetails] = useState<OrderDetailsForDisplay | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails>({
    name: '', cift:'', email: '', eventDate: '', phone: '',
    address: '', city: '', town: '', postalCode: '', note: '',
    organizationName: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Organizasyonlar için state'ler
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [orgLoading, setOrgLoading] = useState<boolean>(false);
  const [orgError, setOrgError] = useState<string>('');

  useEffect(() => {
    const savedPackageJSON = localStorage.getItem('anIkareSelectedPackage');
    const savedQuantitiesJSON = localStorage.getItem('orderQuantities');

    if (!savedPackageJSON) {
      alert("Sepetinizde ürün bulunmamaktadır. Lütfen paket seçimi yapın.");
      navigate('/');
      return;
    }

    if (savedPackageJSON) {
      const savedPackage = JSON.parse(savedPackageJSON);
      const savedQuantities = savedQuantitiesJSON ? JSON.parse(savedQuantitiesJSON) : {};

      // Ana paket ve addonları birleştiriyoruz (Orijinal yapıyı koruduk)
      const typedProducts: ProductWithId[] = [
        ...PRODUCTS, // addonlar
        ...PACKAGE_CATEGORIES.flatMap(c => c.packages) // ana paketler
      ];

      // Ana paketi bulmak için `savedPackage.id` kullanılıyor (Orijinal yapıyı koruduk)
      const mainPackageProduct = typedProducts.find(p => p.id === savedPackage.id);
      if (!mainPackageProduct) {
        alert("Ana paket bulunamadı.");
        navigate('/');
        return;
      }

      // ✅ DÜZELTME 1: Ücretsiz adetleri içeren haritayı oluştur ve TypeScript için indeks imzası ekle
      const isEko = mainPackageProduct.id && mainPackageProduct.id.startsWith('ekonomik');
      const freeQuantitiesMap: { [key: string]: number } = {
        'masa-karti': isEko ? 0 : (mainPackageProduct.freeTableCardQty || 0),
        'tek-yuz-masa-karti': isEko ? (mainPackageProduct.freeTableCardQty || 0) : 0,
        'el-karti': mainPackageProduct.freeHandCardQty || 0,
        'karsilama-panosu': mainPackageProduct.freeBoardQty || 0,
      };

      let itemsSubtotal = 0;
      const packagePrice = mainPackageProduct.price;

      const items = typedProducts
        .map(p => {
          const orderedQuantity = savedQuantities[p.id] || (p.id === mainPackageProduct.id ? 1 : 0);

          if (orderedQuantity === 0) return null; // Sıfır adetlileri atla

          // Ana paket için ücretsiz düşme yapma
          if (p.id === mainPackageProduct.id) {
            itemsSubtotal += packagePrice; // Ana paketin fiyatını toplama ekle
            return {
              name: p.name,
              quantity: 1,
              subtotal: packagePrice
            };
          }

          // Ek ürünler (Addonlar) için ücretsiz adetleri düş
          const freeQuantity = freeQuantitiesMap[p.id] || 0;

          // ✅ DÜZELTME 2: Ücretlendirilecek Adeti Hesapla
          const chargeableQuantity = Math.max(0, orderedQuantity - freeQuantity);

          // ✅ DÜZELTME 3: Subtotal'i sadece ücretlendirilecek adet üzerinden hesapla
          let subtotal = chargeableQuantity * p.price;

          itemsSubtotal += subtotal; // Ek ürünlerin ücretli toplamına ekle

          return {
            name: p.name,
            quantity: orderedQuantity, // Gösterim için sipariş edilen toplam adeti kullan
            subtotal: subtotal // Fiyatlandırmayı ücretli kısımdan yap
          };
        })
        .filter(item => item && item.quantity > 0) as { name: string; quantity: number; subtotal: number }[];


      // ✅ DÜZELTME 4: Toplam zaten itemsSubtotal'de hesaplandı ve kargo eklendi
      


      const shippingFee = 0;
      const total = itemsSubtotal + shippingFee;

      setOrderDetails({
        packageName: mainPackageProduct.name,
        packagePrice: mainPackageProduct.price,
        items,
        subtotal: itemsSubtotal,
        tax: 0,
        shippingFee,
        total,
      });
    }
  }, [navigate]);

  // Organizasyonları çekmek için useEffect
  useEffect(() => {
    const fetchOrganizations = async () => {
      setOrgLoading(true);
      setOrgError('');
      try {
        // ✅ GÜNCELLENDİ: Sadece import edilen fonksiyonu çağırıyoruz.
        const data: Organization[] = await getAllOrganizations();

        // Organizasyon listesini state'e kaydet
        setOrganizations(data);

      } catch (err: any) {
        console.error("Organizasyonlar yüklenemedi:", err);
        // apiService'den gelen hata nesnesinin 'message' alanını kullan
        setOrgError(err.message || 'Organizasyonlar yüklenemedi. Sunucuya erişilemiyor.');
      } finally {
        setOrgLoading(false);
      }
    };

    fetchOrganizations();
  }, []); // Boş dependency array, sadece component mount olduğunda çalışır

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setShippingDetails(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const newErrors: { [key: string]: string } = {};
    const requiredFields: (keyof Omit<ShippingDetails, 'note' | 'city' | 'town' | 'organizationName'>)[] =
      ['name', 'email', 'eventDate', 'phone', 'address', 'postalCode'];

    requiredFields.forEach(field => {
      const value = shippingDetails[field];
      if (!value || String(value).trim() === '') {
        newErrors[field] = 'Bu alanın doldurulması zorunludur.';
      }
    });

    if (!city) newErrors.city = 'Lütfen bir şehir seçin.';
    if (!district) newErrors.town = 'Lütfen bir ilçe seçin.';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setIsLoading(false);
      return;
    }

    const finalShippingDetails: ShippingDetails = {
      ...shippingDetails,
      city,
      town: district,
      organizationName: selectedOrganization || '',
    };

    const customerId = generateUniqueCustomerId();

    try {
      // LocalStorage'dan paket ve adetleri çekiyoruz
      const savedPackageJSON = localStorage.getItem('anIkareSelectedPackage');
      const savedQuantitiesJSON = localStorage.getItem('orderQuantities');
      if (!savedPackageJSON) throw new Error("Sipariş verisi (paket) bulunamadı.");

      const savedPackage = JSON.parse(savedPackageJSON);
      const savedQuantities = savedQuantitiesJSON ? JSON.parse(savedQuantitiesJSON) : {};

      const selectedPrintDesignsJSON = localStorage.getItem('selectedPrintDesigns');
      const selectedPrintDesigns = selectedPrintDesignsJSON
        ? JSON.parse(selectedPrintDesignsJSON)
        : null;

      const selectedMasaDesignId: string | undefined = selectedPrintDesigns?.masaKarti?.id;
      const selectedElDesignId: string | undefined = selectedPrintDesigns?.elKarti?.id;

      const masaDesign = MASA_KARTI_TASARIMLARI.find(d => d.id === selectedMasaDesignId);
      const elDesign = EL_KARTI_TASARIMLARI.find(d => d.id === selectedElDesignId);

      const masaDesignName = masaDesign?.name;
      const elDesignName = elDesign?.name;

      const typedProducts: ProductWithId[] = [
        ...PRODUCTS,
        ...PACKAGE_CATEGORIES.flatMap(c => c.packages)
      ];

      const mainPackageProduct = typedProducts.find(p => p.id === savedPackage.id);
      if (!mainPackageProduct) throw new Error("Ana paket bulunamadı.");

      const isEkoPkg = savedPackage.id && savedPackage.id.startsWith('ekonomik');
      const freeQuantitiesMap: { [key: string]: number } = {
        'masa-karti': isEkoPkg ? 0 : (mainPackageProduct.freeTableCardQty ?? savedPackage.freeTableCardQty ?? 0),
        'tek-yuz-masa-karti': isEkoPkg ? (mainPackageProduct.freeTableCardQty ?? savedPackage.freeTableCardQty ?? 0) : 0,
        'el-karti': mainPackageProduct.freeHandCardQty ?? savedPackage.freeHandCardQty ?? 0,
        'karsilama-panosu': mainPackageProduct.freeBoardQty ?? savedPackage.freeBoardQty ?? 0,
      };

      const cartItems = typedProducts
        .filter(p => savedQuantities[p.id] > 0 || p.id === mainPackageProduct.id)
        .map(p => {
          const quantity = savedQuantities[p.id] || (p.id === mainPackageProduct.id ? 1 : 0);
          const freeQty = freeQuantitiesMap[p.id] || 0;

          let designDetails: any = {};

          // 🔴 MASA KARTI İÇİN TASARIM DETAYI
          if (p.id === 'masa-karti' && selectedMasaDesignId && masaDesign) {
            designDetails = {
              designId: masaDesign.id,
              designName: masaDesign.name,
              // Kullanacağın yere göre ikisini de gönderiyoruz:
              designImageUrl: masaDesign.designImageUrl,   // Tasarım/flat görsel
              productImageUrl: masaDesign.productImageUrl // 3D/mockup görsel
            };
          }

          // 🔴 EL KARTI İÇİN TASARIM DETAYI
          if (p.id === 'el-karti' && selectedElDesignId && elDesign) {
            designDetails = {
              designId: elDesign.id,
              designName: elDesign.name,
              designImageUrl: elDesign.designImageUrl,
              productImageUrl: elDesign.productImageUrl
            };
          }

          return {
            productId: p.id,
            quantity,
            freeQty,
            designDetails
          };
        });

      console.log("Gönderilecek sepet:", cartItems);

      const requestBody = {
        items: cartItems,
        customerId: customerId,
        shippingDetails: finalShippingDetails
      };

      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody), // 💥 BURASI DÜZELTİLDİ
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP Hatası: ${res.status}`);
      }

      const data = await res.json();
      const { cartId } = data;
      if (!cartId) throw new Error("Backend'den geçerli bir Cart ID gelmedi.");

      sessionStorage.setItem('shippingDetails', JSON.stringify(finalShippingDetails));
      localStorage.setItem('anIkareCartId', cartId);

      console.log("Sepet başarıyla oluşturuldu. Cart ID:", cartId);
      console.log("Müşteri ID:", customerId);
      navigate('/odeme');

    } catch (error: any) {
      console.error("Sepet oluşturma hatası:", error);
      alert(`Sipariş oluşturulamadı. Hata: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="bg-rose-50 min-h-screen">
      <SimpleHeader />
      <main className="mx-auto max-w-6xl px-4 pt-24 pb-8">
        <div className="text-center mb-8 bg-rose-100/60 p-4 rounded-lg border border-rose-200">
          <h1 className="text-3xl font-extrabold text-gray-800">Adres Bilgileri</h1>
          <p className="mt-2 text-gray-600">Teslimat ve fatura bilgilerinizi girin.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <form onSubmit={handleSubmit} noValidate className="lg:col-span-2 space-y-6">
            {/* İletişim Bilgileri */}
            <div className="bg-white p-6 rounded-xl shadow-md border">
              <h2 className="text-xl font-bold mb-4 text-gray-800">İletişim Bilgileri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput label="Ad Soyad" name="name" placeholder="Adınız ve Soyadınız" value={shippingDetails.name} onChange={handleInputChange} error={errors.name} required />
                <FormInput label="Çiftin isimleri" name="cift" placeholder="İsimleriniz" value={shippingDetails.cift} onChange={handleInputChange} error={errors.cift} required />
                <FormInput label="E-posta" name="email" placeholder="ornek@mail.com" type="email" value={shippingDetails.email} onChange={handleInputChange} error={errors.email} required />
                <FormInput label="Organizasyon Tarihi" name="eventDate" type="date" value={shippingDetails.eventDate} onChange={handleInputChange} error={errors.eventDate} required />
                <FormInput label="Telefon Numarası" name="phone" placeholder="5XX XXX XX XX" type="tel" value={shippingDetails.phone} onChange={handleInputChange} error={errors.phone} required />
              </div>
            </div>

            {/* Fatura / Teslimat Adresi */}
            <div className="bg-white p-6 rounded-xl shadow-md border">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Fatura / Teslimat Adresi</h2>
              <div className="space-y-4">
                <FormInput label="Adres" name="address" placeholder="Mahalle, cadde, no, ilçe" value={shippingDetails.address} onChange={handleInputChange} error={errors.address} required />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="col-span-1">
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">İl / Şehir</label>
                    <select
                      id="city"
                      name="city"
                      value={city}
                      onChange={e => {
                        setCity(e.target.value);
                        setShippingDetails(prev => ({ ...prev, city: e.target.value, town: '' }));
                        setErrors(prev => ({ ...prev, city: '', town: '' }));
                      }}
                      className={`w-full p-2 border rounded-md ${errors.city ? 'border-red-500' : 'border-gray-300'} focus:ring-rose-500 focus:border-rose-500`}
                    >
                      <option value="">Şehir Seçin *</option>
                      {cities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
                  </div>

                  <div className="col-span-1">
                    <label htmlFor="town" className="block text-sm font-medium text-gray-700 mb-1">İlçe</label>
                    <select
                      id="town"
                      name="town"
                      value={district}
                      onChange={e => {
                        setDistrict(e.target.value);
                        setShippingDetails(prev => ({ ...prev, town: e.target.value }));
                        setErrors(prev => ({ ...prev, town: '' }));
                      }}
                      disabled={!city}
                      className={`w-full p-2 border rounded-md ${errors.town ? 'border-red-500' : 'border-gray-300'} ${!city ? 'bg-gray-100' : 'bg-white'} focus:ring-rose-500 focus:border-rose-500`}
                    >
                      <option value="">İlçe Seçin {city ? '*' : ''}</option>
                      {districts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {errors.town && <p className="mt-1 text-xs text-red-600">{errors.town}</p>}
                  </div>

                  <FormInput label="Posta Kodu" name="postalCode" placeholder="34000" value={shippingDetails.postalCode} onChange={handleInputChange} error={errors.postalCode} required />
                </div>

                {/* YENİ EKLENDİ: Organizasyon Seçim Kutusu */}
                <div className="col-span-1">
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                    Organizasyon Adı (Opsiyonel) Bu seçeneği hizmetimizi organizasyon aracalığı ile tanıştıysanız seçiniz.
                  </label>
                  <select
                    id="organization"
                    name="organization"
                    value={selectedOrganization}
                    onChange={e => {
                      setSelectedOrganization(e.target.value);
                      setErrors(prev => ({ ...prev, organizationName: '' }));
                    }}
                    disabled={orgLoading || !!orgError}
                    className={`w-full p-2 border rounded-md ${errors.organizationName ? 'border-red-500' : 'border-gray-300'} ${orgLoading ? 'bg-gray-100' : 'bg-white'} focus:ring-rose-500 focus:border-rose-500`}
                  >
                    <option value="">Anlaşmalı Organizasyonlar</option>
                    {orgLoading && <option disabled>Yükleniyor...</option>}
                    {organizations.map(org => (
                      <option key={org._id} value={org.name}>{org.name}</option>
                    ))}
                  </select>
                  {orgError && <p className="mt-1 text-xs text-red-600">{orgError}</p>}
                  {errors.organizationName && <p className="mt-1 text-xs text-red-600">{errors.organizationName}</p>}
                </div>

                <div className="col-span-full">
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">Sipariş Notu (Opsiyonel)</label>
                  <textarea
                    id="note"
                    name="note"
                    placeholder="Teslimat ile ilgili notlarınız..."
                    value={shippingDetails.note}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-rose-500 focus:border-rose-500"
                  ></textarea>
                </div>
              </div>
            </div>

            <Button type="submit" size="lg" className="w-full bg-[#e45078] hover:bg-rose-600 text-lg text-white" disabled={isLoading}>
              {isLoading ? 'Sepet Oluşturuluyor...' : 'Ödemeye Devam Et'}
            </Button>
          </form>

          <div className="lg:col-span-1">
            <OrderSummaryCard orderDetails={orderDetails} />
          </div>
        </div>
      </main>
    </div>
  );
}
